import { motion } from "framer-motion";
import { PenTool, Sparkles, ArrowRight } from "lucide-react";
import { IMAGES } from "@/constants/site";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

export default function About() {
  return (
    <section id="artiste" className="relative py-20 md:py-28">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-0 top-1/4 h-[350px] w-[350px] rounded-full bg-violet-700/10 blur-[120px]" />
        <div className="absolute right-10 bottom-0 h-[300px] w-[300px] rounded-full bg-cyan-500/10 blur-[110px]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 md:px-8 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotate: -3 }}
          whileInView={{ opacity: 1, scale: 1, rotate: -2 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative mx-auto w-fit lg:col-span-5"
        >
          <div className="absolute -inset-8 rounded-full bg-gradient-to-tr from-violet-600/25 via-fuchsia-500/20 to-amber-400/25 blur-[60px]" aria-hidden="true" />
          <div className="holo-border relative overflow-hidden rounded-3xl shadow-[0_30px_70px_-20px_rgba(0,0,0,0.8)]">
            <img
              src={IMAGES.artist}
              alt="Sébastien Grosso alias pokebastos06 — sa carte Supporter personnalisée"
              className="h-[420px] w-[300px] object-cover object-top sm:h-[480px] sm:w-[340px]"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,transparent_35%,rgba(255,255,255,0.15)_48%,transparent_60%)]" aria-hidden="true" />
          </div>
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-4 -right-4 flex items-center gap-2.5 rounded-xl border border-white/10 bg-[#100e19]/95 px-4 py-2.5 shadow-xl backdrop-blur-md"
          >
            <Sparkles size={15} className="text-amber-300" />
            <span className="text-sm font-bold text-white">pokebastos06</span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">03 — L'artiste</p>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Moi, c'est <span className="text-holo">Sébastien</span>.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white sm:text-lg">
              Moi, c'est Sébastien Grosso, alias pokebastos06. Né en 1987 et passionné de pop
              culture, je collectionne les cartes Pokémon depuis la cour de récréation.
            </p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white sm:text-lg">
              Aujourd'hui, je fais vivre cette passion à travers l'art : je crée mes propres cartes
              customisées et réalise des tableaux sur mesure pour mettre en valeur ces trésors et
              offrir une vision unique de la collection.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              {[
                { icon: PenTool, label: "Cartes customisées originales" },
                { icon: Sparkles, label: "Tableaux sur mesure" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-[#f4f3f8]"
                >
                  <Icon size={15} className="text-amber-300" />
                  {label}
                </span>
              ))}
            </div>

            <p className="mt-7 font-mono text-xs uppercase tracking-[0.2em] text-[#a09bb5]">
              — Sébastien Grosso · pokebastos06
            </p>

            <button
              type="button"
              data-testid="about-cta-contact"
              onClick={() => scrollToSection("contact")}
              className="btn-holo group mt-8 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-base font-bold text-[#08070d]"
            >
              Créer votre pièce sur mesure
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
